import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import LanguageSwitcher from './LanguageSwitcher';
import { Button } from '../ui/v2';
import { useLang, localizePath, isLocalizedPath, type Lang } from '../../lib/lang';

// [T223 · U2] The header CTA's label. It lives here as a bilingual pair rather
// than an i18next key because `src/i18n/**` is outside this card's write-face
// (wording is a separate card) — the same call Footer.tsx's METHODOLOGY_ENTRY
// made, hub-approved, and the shape `scripts/i18n-audit.mjs` accepts on the
// public surface. design-hybrid.md §6 fixes the destination site-wide:
// the free assessment, never the paid booking.
const ASSESSMENT_CTA: Record<Lang, string> = {
  en: 'Free assessment',
  zh: '免费评估',
};

// ── [T274] the drawer must be able to reach its own bottom ───────────────────
// The header is `sticky top-0`. Open the mobile menu at 719×860 and the header
// grows to 1058px, so 197px of the panel — the language control and the bottom
// CTA among it — hangs below the fold while the panel itself reports
// `max-height: none` / `overflow-y: visible`: there is nothing to scroll.
// Page scroll cannot rescue it either, because a sticky header travels WITH the
// viewport; the overflowing tail stays exactly where it is. (Hub's production
// measurement, 2026-08-02.)
//
// So the panel gets its own scroll box, capped at the space that actually
// exists between its top edge and the bottom of the viewport. The nav bar's
// height is MEASURED rather than assumed, because it is not a constant: at 200%
// text the brand lockup alone takes the bar from 64px to ~88px (T193's whole
// subject), and a hardcoded 64 would leave 24px of menu unreachable — the same
// bug, quieter.
//
// TWO declarations, deliberately. The class carries the `100vh` fallback at the
// measured baseline; the inline style carries the `100dvh` truth (a phone's
// address bar grows and shrinks the viewport under the menu). A browser that
// cannot parse `dvh` drops the inline declaration and is still capped by the
// class; one that can lets the inline style win on specificity.
//
// The class is written as a LITERAL. Tailwind's scanner reads source text, so a
// class assembled through `${}` interpolation produces no CSS at all — the
// fallback would silently be no fallback. `MOBILE_MENU_NAV_FALLBACK_PX` and the
// literal are kept in agreement by a unit assertion, not by hope.
export const MOBILE_MENU_NAV_FALLBACK_PX = 64;
export const MOBILE_MENU_PANEL_CLASS =
  'v2-nav border-t border-v2-line bg-white lg:hidden ' +
  'max-h-[calc(100vh-64px)] overflow-y-auto overscroll-contain';
/** The `100dvh` cap, from the measured bar height (null before first paint). */
export function mobileMenuPanelStyle(navBarHeightPx: number | null): { maxHeight: string } {
  return { maxHeight: `calc(100dvh - ${navBarHeightPx ?? MOBILE_MENU_NAV_FALLBACK_PX}px)` };
}

// On portal/admin surfaces (/intake*, /triage*, /admin* — no /zh URL variant)
// the global switcher is hidden: it would navigate to a dead /zh<portal> path
// (T75), and those surfaces carry their own in-page language toggle. This subtle,
// NON-interactive marker takes its place so assistive tech and hovering users get
// a pointer to that in-page control, and it can never produce a link.
function PortalLangHint() {
  const label = '本页面语言在页面内切换 · Set language within this page';
  return (
    <span className="flex items-center gap-1.5 px-2 py-2 text-slate-400" title={label}>
      <i className="ri-global-line text-lg" aria-hidden="true"></i>
      <span className="sr-only">{label}</span>
    </span>
  );
}

export default function Header() {
  const { t } = useTranslation();
  const location = useLocation();
  const lang = useLang();
  const lp = (p: string) => localizePath(p, lang);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // [T223] the scroll-edge: flush hairline at the top of the page, shadow once
  // the content starts sliding under the glass. Purely presentational, so it
  // starts `false` — a prerendered/SSR pass has no scroll position and must not
  // paint an elevation that the first frame then removes.
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // [T274] The nav bar's live height — the only input the panel's scroll cap
  // needs. Read once at mount and then FOLLOWED: the bar changes height on text
  // zoom, on rotate, and whenever the brand lockup rewraps, and a cap computed
  // from a stale height is a menu with an unreachable tail. `null` until the
  // first measurement so SSR/prerender falls back to the literal baseline
  // instead of inventing a number.
  const navBarRef = useRef<HTMLElement>(null);
  const [navBarHeight, setNavBarHeight] = useState<number | null>(null);

  useEffect(() => {
    const el = navBarRef.current;
    if (!el) return;
    const measure = () => setNavBarHeight(el.getBoundingClientRect().height);
    measure();
    if (typeof ResizeObserver !== 'function') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // The switcher only works on URL-localized (public) routes; on portal/admin
  // surfaces it would build a dead /zh path, so it is scoped out there (T75).
  const showLangSwitcher = isLocalizedPath(location.pathname);

  // [T234] The ground truth's information architecture
  // (docs/design/rebuild-v0b7764f/site/index.html §site-header): five top-level
  // destinations, two of them dropdowns. What changed and why:
  //   • Services grew a submenu — the six service pages already EXIST at
  //     /services/<slug> (src/data/programsData.json) and were reachable only by
  //     scrolling the hub. The parent link still goes to the hub.
  //   • "More" (a shrug) became "Resources" (a promise), and Pricing/Contact —
  //     the two commercial destinations — came out of it onto the top row.
  // Divergences from the ground truth, both deliberate and both on the card:
  //   • Login STAYS (I4: one permanent /intake door, nav + footer). The ground
  //     truth moves it into the footer alone; the constitution outranks it.
  //   • The chrome CTA stays the OUTLINE pill (I29: one solid primary per
  //     screen, and the hero owns it here). The ground truth ships two solid.
  const servicesNav = [
    { name: t('nav.servicesMenu.expressEntry'), href: '/services/express-entry' },
    { name: t('nav.servicesMenu.bcPnpTech'), href: '/services/bc-pnp-tech' },
    { name: t('nav.servicesMenu.spousalSponsorship'), href: '/services/spousal-sponsorship' },
    { name: t('nav.servicesMenu.c11Entrepreneur'), href: '/services/c11-entrepreneur' },
    { name: t('nav.servicesMenu.superVisa'), href: '/services/super-visa' },
    { name: t('nav.servicesMenu.pgwp'), href: '/services/pgwp' },
  ];
  // [T243] Guides took the ground truth's second Resources row (Policy Updates ·
  // Guides · FAQ · Methodology · Testimonials); [T336] the CRS calculator now sits
  // second (hub ruling d, 2026-10-21) and Guides third. T234 left the
  // slot empty on purpose — "this app has `/guides/:slug` but no `/guides` index
  // route, and a nav entry pointing at a 404 is worse than a missing one" — so
  // the row and the route it needs land in the SAME change (src/router/config.tsx
  // + src/pages/guides/page.tsx), never one without the other.
  const resourcesNav = [
    { name: t('nav.policyUpdates'), href: '/policy-updates' },
    { name: t('nav.crsCalculator'), href: '/crs-calculator' },
    { name: t('nav.guides'), href: '/guides' },
    { name: t('nav.faq'), href: '/faq' },
    { name: t('nav.methodology'), href: '/methodology' },
    { name: t('nav.testimonials'), href: '/testimonials' },
  ];
  const isActive = (path: string) => location.pathname === lp(path);
  const servicesActive = isActive('/services') || servicesNav.some((s) => isActive(s.href));
  const resourcesActive = resourcesNav.some((m) => isActive(m.href));

  // The desktop row, in the ground truth's order. `menu` entries render the
  // dropdown; `href` on a `menu` makes its trigger a real link to the hub page
  // (the ground truth does the same for Services) so the parent destination
  // never becomes hover-only.
  const navRow: Array<{ name: string; href?: string; menu?: Array<{ name: string; href: string }>; active?: boolean }> = [
    { name: t('nav.about'), href: '/about' },
    { name: t('nav.services'), href: '/services', menu: servicesNav, active: servicesActive },
    { name: t('nav.pricing'), href: '/pricing' },
    { name: t('nav.pathwayFinder'), href: '/pathway-finder' },
    { name: t('nav.more'), menu: resourcesNav, active: resourcesActive },
    { name: t('nav.contact'), href: '/contact' },
  ];
  // The hamburger mirrors the desktop row exactly — same destinations, same
  // order, flattened (a dropdown inside a drawer is a tap for nothing). `child`
  // is the only thing the flattening loses, so it is carried explicitly: without
  // it the six service pages read as six more top-level destinations.
  const mobileNav: Array<{ name: string; href: string; child?: boolean }> = [
    { name: t('nav.home'), href: '/' },
    { name: t('nav.about'), href: '/about' },
    { name: t('nav.services'), href: '/services' },
    ...servicesNav.map((s) => ({ ...s, child: true })),
    { name: t('nav.pricing'), href: '/pricing' },
    { name: t('nav.pathwayFinder'), href: '/pathway-finder' },
    // Resources' four destinations stay top-level here rather than under a
    // label that is not itself a destination — the drawer already reads as one
    // list, and a header you cannot tap is just a row that fails.
    ...resourcesNav,
    { name: t('nav.contact'), href: '/contact' },
  ];

  return (
    // [T223 · U2] v2 glass chrome — 92% white + blur(12px) saturate(180%), the
    // hairline underneath, the elevation only once the page has scrolled. The
    // surface and all three of its a11y degradations live together in the
    // index.css v2 layer (`.v2-header-glass`), per I33.
    //
    // I3 IS NOT NEGOTIATED AWAY BY THE NEW SKIN. The three protected pieces —
    // the teal-500→600 gradient tile, its `shadow-md`, and the Login door —
    // are carried through this rewrite byte-for-byte. Where the ground truth
    // and the constitution disagree (the rebuild moves Login into the footer
    // and drops the gradient mark for a flat logo file), the constitution wins
    // and the divergence is written on the card, never applied silently.
    <header
      data-v2
      className={`v2-header-glass sticky top-0 z-50 border-b border-v2-line ${
        scrolled ? 'v2-header-scrolled' : ''
      }`}
    >
      {/* [T193] `lg:flex-wrap` IS the text-zoom collapse shape. Above lg the three
          clusters measure 365 + 1051 + 819px at a 200% root font size against
          1137px of available width, so they cannot share a row — wrapping lets the
          header grow TALLER instead of running off-canvas (WCAG 1.4.4 resize-text).
          At 100% they measure 227 + 526 + 409 = 1162px and still fit one row, so
          the baseline is untouched; `gap-4` already supplies the row gap.
          Scoped to lg (the same desktop/mobile split the rest of this component
          uses) because BELOW lg the row is just brand + menu button: there the
          brand truncating is the better collapse, and wrapping would only push the
          menu button onto a second row. */}
      <nav ref={navBarRef} className="v2-nav mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-3 lg:flex-wrap lg:px-8">
        {/* Logo — the teal gradient tile never shrinks (I3); only the wordmark may
            truncate, so it can never shove the menu button off-canvas (a 375px
            phone at 200% text leaves the brand 280px for 365px of type). */}
        <Link to={lp('/')} className="flex min-w-0 items-center gap-2.5">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 shadow-md">
            <i className="ri-compass-3-line text-2xl text-white"></i>
          </div>
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-xl font-bold leading-tight tracking-tight text-gray-900" style={{ fontFamily: '"Playfair Display", serif' }}>
              Think Forward
            </span>
            <span className="truncate text-[11px] font-medium uppercase leading-tight tracking-wider text-teal-600" style={{ fontFamily: '"Montserrat", sans-serif', letterSpacing: '0.08em' }}>
              Immigration Consulting
            </span>
          </div>
        </Link>

        {/* Desktop navigation: the ground truth's row — links and dropdowns
            interleaved in ONE ordered list, so the reading order is the IA
            (About · Services▾ · Pricing · Pathway Finder · Resources▾ · Contact)
            rather than "everything flat, then whatever was left over".
            [T193] `flex-wrap` + `gap-y-2`: at 200% text this column alone measures
            1019px against 945px of line width on a 1024px canvas, so it must be
            able to wrap WITHIN itself — wrapping also drops its min-content width
            to the widest single link, which is what lets it be squeezed onto the
            line at all. The links keep `whitespace-nowrap` (no mid-label breaks).
            [T234] The gap tightened from 24/28px to 20px because the row grew:
            measured at 1440 with the ground truth's IA, the three clusters came
            to 227 + 614 + 369 = 1210px against 1216px of line — plus 2×16px of
            inter-cluster gap, which put the right-hand cluster (language, Login,
            CTA) onto a SECOND ROW in English while 中文 still fitted on one. A
            two-row header is not the face this card was asked for. 20px buys
            40px back across the five gaps and leaves ~26px of headroom; the
            `flex-wrap` that T193 added for 200% text is untouched, so the zoom
            behaviour it protects still happens exactly where it did. */}
        <div className="hidden flex-wrap items-center gap-x-5 gap-y-2 lg:flex">
          {navRow.map((item) =>
            item.menu ? (
              /* Dropdown (hover) */
              <div key={item.name} className="group relative">
                {item.href ? (
                  <Link
                    to={lp(item.href)}
                    className={`flex items-center gap-1 whitespace-nowrap text-sm font-semibold transition-colors ${
                      item.active ? 'text-teal-600' : 'text-gray-700 hover:text-teal-600'
                    }`}
                  >
                    {item.name}
                    <i className="ri-arrow-down-s-line text-base transition-transform group-hover:rotate-180"></i>
                  </Link>
                ) : (
                  <button
                    type="button"
                    className={`flex items-center gap-1 whitespace-nowrap text-sm font-semibold transition-colors ${
                      item.active ? 'text-teal-600' : 'text-gray-700 hover:text-teal-600'
                    }`}
                  >
                    {item.name}
                    <i className="ri-arrow-down-s-line text-base transition-transform group-hover:rotate-180"></i>
                  </button>
                )}
                {/* pt-3 forms a hover bridge so the menu doesn't close in the gap.
                    [T223] The panel is 320px wide (design-hybrid §6) and its rows
                    never wrap: at 200% text a 208px panel broke "Policy Updates"
                    across two lines and the taller rows pushed the last item under
                    the fold. Width + `whitespace-nowrap` are the pair — either one
                    alone still wraps or still clips.
                    [T234] `left-0` for a panel that is NOT the last item: the
                    Services menu sits mid-row, and right-aligning it there hangs
                    a 320px panel back over About. The last panel (Resources) keeps
                    the right alignment that stops it running off the canvas. */}
                <div
                  className={`absolute ${item.href ? 'left-0' : 'right-0'} top-full hidden pt-3 group-hover:block`}
                >
                  <div className="min-w-[320px] overflow-hidden rounded-v2-card border border-v2-line bg-white py-2 shadow-v2-lift">
                    {item.menu.map((sub) => (
                      <Link
                        key={sub.href}
                        to={lp(sub.href)}
                        className={`block whitespace-nowrap px-4 py-2.5 text-sm font-medium transition-colors ${
                          isActive(sub.href) ? 'bg-teal-50 text-teal-700' : 'text-gray-700 hover:bg-slate-50 hover:text-teal-600'
                        }`}
                      >
                        {sub.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <Link
                key={item.href}
                to={lp(item.href!)}
                className={`whitespace-nowrap text-sm font-semibold transition-colors ${
                  isActive(item.href!) ? 'text-teal-600' : 'text-gray-700 hover:text-teal-600'
                }`}
              >
                {item.name}
              </Link>
            ),
          )}
        </div>

        {/* Right: language switcher + Login + CTA.
            [T193] `flex-shrink-0` was half of the 2× deadlock — it pinned this
            cluster at its 819px max-content width and marched Login and the CTA off
            the canvas. Dropping it plus `flex-wrap` lets the cluster take its own
            row (and, if even that is too narrow, stack within itself) so BOTH the
            single /intake door (I4) and the assessment CTA stay inside the viewport
            and hittable at 200% text. */}
        <div className="hidden flex-wrap items-center gap-3 lg:flex">
          {showLangSwitcher ? <LanguageSwitcher /> : <PortalLangHint />}
          {/* Portal entry — /intake has no /zh variant, so never lp() it (T75) */}
          <Link
            to="/intake"
            className="whitespace-nowrap text-sm font-semibold text-gray-700 transition-colors hover:text-teal-600"
          >
            {t('nav.login')}
          </Link>
          {/* [T223] The nav CTA is the teal pill of design-hybrid §6 in SECONDARY
              form. The ground truth ships it solid, which would put TWO solid
              primaries on the homepage's first screen (nav + hero) — I29 allows
              exactly one, and the card's own acceptance measures it. So the
              persistent chrome CTA yields the solid state to the hero's, keeps
              the pill shape and the teal, and the divergence is on the card. */}
          <Button as={Link} to={lp('/pathway-finder')} variant="secondary" size="sm" className="whitespace-nowrap">
            {ASSESSMENT_CTA[lang]}
          </Button>
        </div>

        {/* Mobile menu button */}
        <button
          type="button"
          className="-m-2.5 inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-md text-gray-700 lg:hidden"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          <span className="sr-only">{t('header.openMenu')}</span>
          <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
        </button>
      </nav>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className={MOBILE_MENU_PANEL_CLASS} style={mobileMenuPanelStyle(navBarHeight)} data-mobile-menu-panel="">
          <div className="space-y-1 px-5 pb-4 pt-2">
            {mobileNav.map((item) => (
              <Link
                key={item.href}
                to={lp(item.href)}
                className={`block rounded-lg py-2.5 text-base font-semibold ${
                  item.child ? 'pl-7 pr-3 text-[0.95rem] font-medium' : 'px-3'
                } ${isActive(item.href) ? 'bg-teal-50 text-teal-600' : 'text-gray-700 hover:bg-gray-50'}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}
            {/* Portal entry — /intake has no /zh variant, so never lp() it (T75) */}
            <Link
              to="/intake"
              className="block rounded-lg px-3 py-2.5 text-base font-semibold text-gray-700 hover:bg-gray-50"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('nav.login')}
            </Link>
            {/* [T274] INLINE here, dropdown on the desktop row above. A popup
                anchored `top-full` at the bottom of a sticky drawer opens below
                the fold and cannot be tapped at all — see the LanguageSwitcher
                header comment for the production measurement. */}
            <div className="px-3 pt-2">
              {showLangSwitcher ? <LanguageSwitcher variant="inline" /> : <PortalLangHint />}
            </div>
            <Button
              as={Link}
              to={lp('/pathway-finder')}
              variant="secondary"
              block
              className="mt-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              {ASSESSMENT_CTA[lang]}
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
